import { getCurrentLanguage } from "@/hooks/use-language";
import { getShopFromLocalStorage } from "@/hooks/use-shop";
import { getCashAgentUrl, getFeatureFlags, getServerUrl } from "./runtime-config";

const STORAGE_KEY = "kiosk-tracked-session";

type TrackedSessionState = {
  id: string;
  startedAt: string;
};

type TrackedEvent = {
  eventType: string;
  routePath?: string;
  userId?: number | string;
  machineId?: number;
  dryerBundleId?: number;
  reservationId?: number | string;
  kioskPaymentNonceKey?: string;
  couponNonceKey?: string;
  payload?: unknown;
};

function getTrackingUrls() {
  return getFeatureFlags().sessionTracking
    ? [getCashAgentUrl(), getServerUrl()]
    : [];
}

async function trackingFetch(path: string, init: RequestInit) {
  const shop = getShopFromLocalStorage();
  const urls = getTrackingUrls();

  for (let i = 0; i < urls.length; i++) {
    try {
      const response = await fetch(`${urls[i]}${path}`, {
        ...init,
        headers: {
          "Content-Type": "application/json",
          "lang-key": getCurrentLanguage(),
          ...(shop?.kioskKey ? { "kiosk-key": shop.kioskKey } : {}),
          ...(shop?.currencyCode
            ? {
                "country-code": shop.currencyCode === "EUR" ? "sk" : "hu",
              }
            : {}),
          ...init.headers,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Tracking request failed with status ${response.status}`,
        );
      }

      return response;
    } catch (error) {
      if (i === urls.length - 1) {
        throw error;
      }
    }
  }

  return undefined;
}

export function getTrackedSessionState(): TrackedSessionState | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as TrackedSessionState;
    if (!parsed.id || !parsed.startedAt) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function setTrackedSessionState(state: TrackedSessionState | null) {
  if (!state) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function getTrackedSessionId() {
  return getTrackedSessionState()?.id;
}

export async function ensureTrackedSession(routePath?: string) {
  const existing = getTrackedSessionState();
  if (existing) {
    return existing;
  }

  const shop = getShopFromLocalStorage();
  if (!shop) {
    return null;
  }

  const state = {
    id: crypto.randomUUID(),
    startedAt: new Date().toISOString(),
  } satisfies TrackedSessionState;

  setTrackedSessionState(state);

  await trackingFetch("/kiosk-tracking/sessions", {
    method: "POST",
    body: JSON.stringify({
      records: [
        {
          id: state.id,
          shop_id: shop.shopId,
          started_at: state.startedAt,
          initial_language: getCurrentLanguage(),
          current_language: getCurrentLanguage(),
          last_event_at: state.startedAt,
          ...(routePath ? { last_route_path: routePath } : {}),
        },
      ],
    }),
  }).catch((error) => {
    console.error("Failed to create tracked kiosk session", error);
  });

  return state;
}

export async function updateTrackedSession(
  patch: Partial<{
    current_language: string;
    user_id: number | string;
    last_event_at: string;
    last_route_path: string;
    ended_at: string;
    ended_reason: string;
  }>,
) {
  const state = getTrackedSessionState();
  const shop = getShopFromLocalStorage();
  if (!state || !shop) {
    return;
  }

  await trackingFetch("/kiosk-tracking/sessions", {
    method: "POST",
    body: JSON.stringify({
      records: [
        {
          id: state.id,
          shop_id: shop.shopId,
          started_at: state.startedAt,
          ...patch,
        },
      ],
    }),
  }).catch((error) => {
    console.error("Failed to update tracked kiosk session", error);
  });
}

export async function endTrackedSession(reason = "returned-to-start-screen") {
  const state = getTrackedSessionState();
  if (!state) {
    return;
  }

  const endedAt = new Date().toISOString();
  await updateTrackedSession({
    ended_at: endedAt,
    ended_reason: reason,
    last_event_at: endedAt,
    current_language: getCurrentLanguage(),
  });
  setTrackedSessionState(null);
}

export async function recordTrackedEvent(event: TrackedEvent) {
  const state = await ensureTrackedSession(event.routePath);
  const shop = getShopFromLocalStorage();
  if (!state || !shop) {
    return;
  }

  const eventAt = new Date().toISOString();
  await trackingFetch("/kiosk-tracking/events", {
    method: "POST",
    body: JSON.stringify({
      records: [
        {
          id: crypto.randomUUID(),
          kiosk_session_id: state.id,
          shop_id: shop.shopId,
          event_type: event.eventType,
          event_at: eventAt,
          ...(event.routePath ? { route_path: event.routePath } : {}),
          ...(event.userId ? { user_id: Number(event.userId) } : {}),
          ...(event.machineId ? { machine_id: event.machineId } : {}),
          ...(event.dryerBundleId
            ? { dryer_bundle_id: event.dryerBundleId }
            : {}),
          ...(event.reservationId
            ? { reservation_id: Number(event.reservationId) }
            : {}),
          ...(event.kioskPaymentNonceKey
            ? { kiosk_payment_nonce_key: event.kioskPaymentNonceKey }
            : {}),
          ...(event.couponNonceKey
            ? { coupon_nonce_key: event.couponNonceKey }
            : {}),
          ...(event.payload !== undefined ? { payload: event.payload } : {}),
        },
      ],
    }),
  }).catch((error) => {
    console.error("Failed to record tracked kiosk event", error);
  });

  await updateTrackedSession({
    last_event_at: eventAt,
    current_language: getCurrentLanguage(),
    ...(event.routePath ? { last_route_path: event.routePath } : {}),
    ...(event.userId ? { user_id: Number(event.userId) } : {}),
  });
}

export function restoreTrackedSessionState(state: TrackedSessionState | null) {
  setTrackedSessionState(state);
}
